"use client";

import type { Variants } from "framer-motion";
import { motion } from "framer-motion";

import { useLanguage } from "@/contexts/LanguageContext";
import { useTranslation } from "@/hooks/useTranslation";
import { bankInformation } from "@/lib/contact/bankInfo";
import { contactInformation } from "@/lib/contact/contact";
import type { BankInfo, Contact as ContactModel } from "@/payload-types";
import type { PayLoadData } from "@/utils/payloadUtil";

type Props = {
  contact: PayLoadData<ContactModel> | undefined;
  bankInfo: PayLoadData<BankInfo> | undefined;
};

const Contact = ({ contact, bankInfo }: Props) => {
  const { t } = useTranslation();
  const { language } = useLanguage();

  const itemVariants: Variants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.4,
        ease: "easeOut",
      },
    },
  };

  return (
    <section id="contact" className="pt-0">
      <div className="container mx-auto">
        <motion.div
          className="grid grid-cols-1 gap-12 lg:grid-cols-2"
          initial="hidden"
          animate="visible"
          variants={itemVariants}
        >
          {/* Contact Information */}
          <div>
            <div className="mb-8 h-full bg-white p-8 shadow-md">
              <h2 className="mb-6 border-b border-gray-100 pb-3 text-xl font-bold">
                {t({ de: "Kontaktinformationen", en: "Contact Information" })}
              </h2>
              <div className="space-y-5">
                {contactInformation.map((info, index) => (
                  <motion.div
                    key={index}
                    className="flex items-start"
                    whileHover={{ x: 3 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className="mt-1 shrink-0 rounded-full bg-primary-100 p-2">
                      {info.icon}
                    </div>
                    <div className="ml-4">
                      <h3 className="text-sm font-medium text-gray-500">
                        {t(info.label)}
                      </h3>
                      <p className="mt-1 whitespace-pre-line font-medium text-gray-800">
                        {(
                          contact?.[language]?.[
                            info.key as keyof ContactModel
                          ] || ""
                        )
                          .toString()
                          .replaceAll("\\n", "\n")}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
          <div>
            <div className="mb-8 h-full bg-white p-8 shadow-md">
              <h2 className="mb-6 border-b border-gray-100 pb-3 text-xl font-bold">
                {t({ de: "Bankverbindung", en: "Bank details" })}
              </h2>
              <div className="space-y-5">
                {bankInformation.map((info, index) => (
                  <motion.div
                    key={index}
                    className="flex items-start"
                    whileHover={{ x: 3 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className="mt-1 shrink-0 rounded-full bg-primary-100 p-2">
                      {info.icon}
                    </div>
                    <div className="ml-4">
                      <h3 className="text-sm font-medium text-gray-500">
                        {t(info.label)}
                      </h3>
                      <p className="mt-1 font-medium text-gray-800">
                        {bankInfo?.[language]?.[info.key as keyof BankInfo] ||
                          ""}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;
